// src/entities/currentHive/store.ts
import {create} from 'zustand';
import {$api} from 'shared/api';
import {useHivesStore} from 'entities/hives';
import {CurrentHiveResponse, CurrentHiveState} from './types';
import {DeviceRow} from 'entities/devices/model/types.ts';

export const useCurrentHiveStore = create<CurrentHiveState>((set, get) => ({
  currentHiveId: '',
  currentHive: null,
  isLoading: false,
  error: null,
  hiveDevices: [],

  setCurrentHiveId: (id: string) => set({currentHiveId: id}),

  fetchById: async (id?: string) => {
    const hiveId = id ?? get().currentHiveId;
    if (!hiveId) return null;
    set({isLoading: true, error: null});
    try {
      const [hiveRes, devicesRes] = await Promise.all([
        $api.get(`/hives/${hiveId}`),
        $api.get(`/collector/devices`, {
          params: {
            hiveId,
          },
        }),
      ]);
      const hiveData: CurrentHiveResponse = hiveRes?.data ?? null;
      const devicesData: DeviceRow[] = devicesRes?.data ?? [];

      set({
        currentHive: {
          ...hiveData,
          isSmart: devicesData.length > 0,
          devices: devicesData.length,
        },
        currentHiveId: hiveId,
        isLoading: false,
        hiveDevices: devicesData,
      });
      return hiveData;
    } catch {
      set({isLoading: false, error: 'Ошибка при загрузке данных улья'});
      return null;
    }
  },

  updateHive: async (patch) => {
    const hiveId = get().currentHiveId;
    const current = get().currentHive;
    if (!hiveId || !current) return null;

    set({isLoading: true, error: null});

    const payload = {
      number: Number((patch as any)?.number ?? current.number ?? 0),
      isSmart: current.isSmart,
      uterus: {
        year: Number((patch as any)?.uterus?.year ?? current.uterus?.year ?? 0),
        mark: Number((patch as any)?.uterus?.mark ?? current.uterus?.mark ?? 0),
        breed: String((patch as any)?.uterus?.breed ?? current.uterus?.breed ?? ''),
        color: String((patch as any)?.uterus?.color ?? current.uterus?.color ?? ''),
      },
      frames: Number((patch as any)?.frames ?? current.frames ?? 0),
      strength: Number((patch as any)?.strength ?? current.strength ?? 0),
      brood: Number((patch as any)?.brood ?? current.brood ?? 0),
      happiness: Number((patch as any)?.happiness ?? current.happiness ?? 0),
      attachmentIds: (patch as any)?.attachments ?? current.attachments.map((el) => el.id) ?? [],
    };

    try {
      const res = await $api.put(`/hives/${hiveId}`, payload);
      const data: CurrentHiveResponse = res?.data ?? null;
      set({currentHive: data, isLoading: false});
      const hivesStore = useHivesStore.getState();
      const last = hivesStore.lastParams;
      if (last) await hivesStore.getHivesFromServer(last, true);
      return data;
    } catch {
      set({isLoading: false, error: 'Ошибка при обновлении данных улья'});
      return null;
    }
  },

  deleteHive: async () => {
    const hiveId = get().currentHiveId;
    if (!hiveId) return false;
    set({isLoading: true, error: null});
    try {
      await $api.delete(`/hives/${hiveId}`);
      const hivesStore = useHivesStore.getState();
      const last = hivesStore.lastParams;
      if (last) await hivesStore.getHivesFromServer(last, true);
      await get().fetchById(hiveId);
      set({isLoading: false});
      return true;
    } catch {
      const hivesStore = useHivesStore.getState();
      const last = hivesStore.lastParams;
      if (last) await hivesStore.getHivesFromServer(last, true);
      set({isLoading: false});
      return false;
    }
  },

  restoreHive: async () => {
    const hiveId = get().currentHiveId;
    if (!hiveId) return false;
    set({isLoading: true, error: null});
    try {
      await $api.patch(`/hives/${hiveId}/restore`);
      const hivesStore = useHivesStore.getState();
      const last = hivesStore.lastParams;
      if (last) await hivesStore.getHivesFromServer(last, true);
      await get().fetchById(hiveId);
      set({isLoading: false});
      return true;
    } catch {
      const hivesStore = useHivesStore.getState();
      const last = hivesStore.lastParams;
      if (last) await hivesStore.getHivesFromServer(last, true);
      set({isLoading: false});
      return false;
    }
  },

  clear: () => set({currentHive: null, currentHiveId: '', error: null}),
}));
